import {
  getApiPlatformBaseUrl,
  getCacheMiddlewareUrl,
  getCleanApiPlatformBaseUrl,
  getCleanCacheMiddlewareUrl,
  getCurrentAppNameForRequestHeader,
} from '@sweetspot/shared/util/functions'
import type { Middleware } from 'openapi-fetch'

export const sunsetMiddleware: Middleware = {
  async onResponse({ response }) {
    const sunsetDate = response.headers.get('Sunset')
    const sunsetReason = response.headers.get('Deprecated-Reason')

    if (sunsetDate) {
      console.log(
        '%c Sunset Notice for endpoint: %s \n Sunset Date: %s \n Sunset Reason: %s',
        'color: black; background-color: yellow; padding: 2px; display: block; float: left',
        response.url.replace('http:', '').replace('https:', ''),
        sunsetDate,
        sunsetReason || 'Unknown'
      )
    }

    return undefined
  },
}

export const addEmptyBodyToPutPostRequestsMiddleware: Middleware = {
  async onRequest({ request }) {
    if (!request.body && (request.method === 'PUT' || request.method === 'POST')) {
      return new Request(request, {
        body: JSON.stringify({}),
      })
    }
    return undefined
  },
}

export const checkForEmptyBodyMiddleware: Middleware = {
  async onResponse({ response }) {
    if (
      response?.ok &&
      response.headers.get('Content-Type')?.includes('application/json') &&
      response?.status === 202
    ) {
      try {
        await response.clone().json()
      } catch (e) {
        const { body, ...rest } = response
        return new Response(null, {
          ...rest,
          status: 204,
        })
      }
    }
    return undefined
  },
}

let cacheFeatureFlagValue: boolean | null = null
let initialFeatureFlagsPromiseResolve: (() => void) | null = null
let initialFeatureFlagsPromise: Promise<void> | null = new Promise(
  (resolve) => (initialFeatureFlagsPromiseResolve = resolve)
)
export const CACHE_CONTROL_HEADER = 'X-Cache-Control'
const APPLICATION_ORIGIN_HEADER = 'X-Application-Origin'

export const cacheMiddleware: Middleware = {
  async onRequest({ request }) {
    const isFeatureFlagsRequest = request?.url?.endsWith('/features')
    /**
     * Make sure initial feature flag is loaded
     */
    if (cacheFeatureFlagValue === null && initialFeatureFlagsPromise && !isFeatureFlagsRequest) {
      try {
        await initialFeatureFlagsPromise
      } catch (err) {
        console.error('Failed to await initial feature flags:', err)
        // Continue
      }
    }

    if (cacheFeatureFlagValue === true && !isFeatureFlagsRequest && request.method === 'GET') {
      const currentApplicationName = getCurrentAppNameForRequestHeader()
      const cacheMiddlewareBaseUrl = getCleanCacheMiddlewareUrl()
      const apiBaseUrl = getCleanApiPlatformBaseUrl()
      const cacheControlHeaderValue = request.headers.get(CACHE_CONTROL_HEADER)

      if (!cacheMiddlewareBaseUrl || !apiBaseUrl) return undefined
      const newUrl = request?.clone().url.replace(apiBaseUrl, cacheMiddlewareBaseUrl)
      const newRequest = new Request(newUrl, request)

      if (cacheControlHeaderValue !== 'no-cache') {
        newRequest.headers.delete(CACHE_CONTROL_HEADER)
      }
      newRequest.headers.append(APPLICATION_ORIGIN_HEADER, currentApplicationName)
      return newRequest
    } else {
      // Normal API does not support this header, so we need to remove it
      request.headers.delete(CACHE_CONTROL_HEADER)
    }

    return undefined
  },
  async onResponse({ response }) {
    if (response?.url?.endsWith('/features')) {
      const clone = response?.clone()
      const data = await clone?.json()
      cacheFeatureFlagValue = data?.['cache-middleware'] ?? false
      if (initialFeatureFlagsPromiseResolve) {
        initialFeatureFlagsPromiseResolve()
      }
      initialFeatureFlagsPromiseResolve = null
      initialFeatureFlagsPromise = null
    }
    return undefined
  },
}
